import React, { useContext, useEffect, useMemo, useState } from 'react';

import { CombatContext } from '../contexts/CombatContext';
import { ActionPill, StatusBadge, useLayeredModal } from './ui/LayeredWorkspace';
import { runtimeContextPayload } from './combatContextIdentity';
import {
  ensureSharingContext, fetchInformationCards, shareInformationCard, updateInformationStatus,
} from './sharingApi';

export const INFORMATION_TYPES = [
  'TREASURE_FOUND', 'ITEM_ACQUIRED', 'ITEM_STOLEN', 'WEATHER', 'DISCOVERY', 'CLUE', 'LOCATION',
  'OBJECTIVE', 'HAZARD', 'FACTION', 'NPC_INFORMATION', 'MONSTER_FACT', 'COMPANION_INFORMATION',
  'CAMPAIGN_FACT', 'CUSTOM_FACTUAL_INFORMATION',
];
const SOURCES = ['COMBAT_PRO', 'TREASURE', 'INVENTORY', 'WEATHER', 'ENCOUNTER', 'GENERATOR', 'COMPANION', 'PRIVATE_MESSAGE', 'CUSTOM'];
const FILTERS = ['ALL', 'CURRENT_SESSION', 'TREASURE', 'ITEMS', 'CLUES', 'WEATHER', 'LOCATIONS', 'OBJECTIVES', 'DISCOVERIES', 'PRIVATE'];
const LIVE_TYPES = ['WEATHER', 'OBJECTIVE', 'HAZARD'];
const LIVE_STATES = ['ACTIVE', 'COMPLETE', 'FAILED', 'EXPIRED'];

function label(value) { return String(value || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, function (letter) { return letter.toUpperCase(); }); }

function normaliseCard(card, mode) {
  if (mode === 'player') return card;
  return {
    ...card, type: card.card_type, source: card.source_kind,
    projection: card.player_projection, structured_content: card.structured_content,
  };
}

function InformationCard({ card, mode, onStatus }) {
  const value = normaliseCard(card, mode);
  const projection = value.projection || {};
  const facts = projection.player_safe_facts || [];
  const structured = value.structured_content || {};
  const live = value.live_state || {};
  const status = live.information_status || value.status || 'ACTIVE';
  return <article className="cp-information-card">
    <div className="cp-information-card-head"><div><span>{label(value.type)}</span><strong>{value.title}</strong></div><StatusBadge tone={String(status).toLowerCase()}>{label(status)}</StatusBadge></div>
    {facts.map(function (fact, index) { return <p key={index}>{typeof fact === 'string' ? fact : JSON.stringify(fact)}</p>; })}
    <dl>{Object.keys(structured).map(function (key) { const item=structured[key]; if (item === '' || item === null || item === undefined || (Array.isArray(item) && !item.length)) return null; return <div key={key}><dt>{label(key)}</dt><dd>{Array.isArray(item) ? item.join(', ') : String(item)}</dd></div>; })}</dl>
    {value.reference_ids && Object.keys(value.reference_ids).length ? <small>References: {Object.keys(value.reference_ids).map(function (key) { return label(key) + ' ' + [].concat(value.reference_ids[key]).join(', '); }).join(' · ')}</small> : null}
    <small>{label(value.source)} · {label(value.visibility)}</small>
    {mode === 'dm' && LIVE_TYPES.indexOf(value.type) >= 0 ? <div className="cp-action-row">{LIVE_STATES.map(function (next) { return <ActionPill key={next} primary={status === next} onClick={function () { onStatus(value, next); }}>{label(next)}</ActionPill>; })}</div> : null}
  </article>;
}

function InformationReference({ mode, campaignId, encounterId, contextPlayers }) {
  const [cards, setCards] = useState([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [notice, setNotice] = useState('Loading authorised information…');
  const usefulFilters = useMemo(function () { return FILTERS.filter(function (value) { return value !== 'CURRENT_SESSION' || encounterId; }); }, [encounterId]);

  async function refresh(nextQuery, nextFilter) {
    if (!campaignId) return;
    try {
      const result = await fetchInformationCards(mode, campaignId, nextQuery === undefined ? query : nextQuery, nextFilter || filter, { encounter_id: encounterId });
      setCards(result.cards || []); setNotice((result.cards || []).length + ' authorised information card' + ((result.cards || []).length === 1 ? '' : 's') + '.');
    } catch (error) { setNotice('Information could not be loaded for this campaign.'); }
  }

  useEffect(function () { refresh('', 'ALL');
    // The reference reloads when its server-authorised campaign changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  async function setLiveStatus(card, next) {
    try { await updateInformationStatus(card.id, card.version, next); await refresh(); }
    catch (error) { setNotice('The live information card changed. Refresh and try again.'); }
  }

  return <>
    <div className="cp-information-search"><label>Search authorised information<input value={query} onChange={function (event) { setQuery(event.target.value); }} onKeyDown={function (event) { if (event.key === 'Enter') refresh(); }} /></label><ActionPill primary onClick={function () { refresh(); }}>Search</ActionPill></div>
    <div className="cp-information-filters" aria-label="Information filters">{usefulFilters.map(function (value) { return <ActionPill key={value} primary={filter === value} onClick={function () { setFilter(value); refresh(query, value); }}>{label(value)}</ActionPill>; })}</div>
    <p className="cp-visibility-notice" role="status">{notice}</p>
    <div className="cp-information-grid">{cards.map(function (card) { return <InformationCard key={card.id} card={card} mode={mode} contextPlayers={contextPlayers} onStatus={setLiveStatus} />; })}</div>
  </>;
}

export function DmInformationWorkspace() {
  const { state } = useContext(CombatContext);
  const [context, setContext] = useState(null);
  const [type, setType] = useState('DISCOVERY');
  const [source, setSource] = useState('COMBAT_PRO');
  const [scope, setScope] = useState('PARTY');
  const [recipients, setRecipients] = useState([]);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [detail, setDetail] = useState('');
  const [items, setItems] = useState('');
  const [currency, setCurrency] = useState('');
  const [ownership, setOwnership] = useState('');
  const [referenceIds, setReferenceIds] = useState('');
  const [revision, setRevision] = useState(0);
  const [notice, setNotice] = useState('Connecting Information authority…');

  useEffect(function () {
    let live = true;
    ensureSharingContext(runtimeContextPayload(state)).then(function (identity) { if (live) { setContext(identity); setNotice('Master Information authority ready.'); } }).catch(function () { if (live) setNotice('Information authority could not connect.'); });
    return function () { live = false; };
    // Identity is established once per modal opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleRecipient(id) { setRecipients(function (current) { return current.indexOf(id) >= 0 ? current.filter(function (value) { return value !== id; }) : current.concat([id]); }); }

  async function publish() {
    if (!context || !title.trim() || !summary.trim()) return;
    let selected = recipients;
    if (scope === 'SINGLE_PLAYER') selected = recipients.slice(0, 1);
    if ((scope === 'SINGLE_PLAYER' || scope === 'SELECTED_PLAYERS') && !selected.length) { setNotice('Choose at least one recipient.'); return; }
    try {
      await shareInformationCard({
        campaign_id: context.campaign_id, title: title.trim(), card_type: type,
        source_kind: source, granularity: 'WHOLE_CARD', visibility: scope,
        recipient_player_ids: scope === 'PARTY' || scope === 'DM_ONLY' ? [] : selected,
        session_id: null, encounter_id: context.encounter_id || null,
        player_projection: { display_cards: [{ title: title.trim(), summary: summary.trim() }], player_safe_facts: [summary.trim()] },
        dm_projection: {},
        structured_content_json: {
          context: detail.trim(), items: items.split('\n').map(function (value) { return value.trim(); }).filter(Boolean),
          currency: currency.trim(), ownership_state: ownership.trim(),
        },
        reference_ids_json: { item_ids: referenceIds.split(',').map(function (value) { return value.trim(); }).filter(Boolean) },
      });
      setTitle(''); setSummary(''); setDetail(''); setItems(''); setCurrency(''); setOwnership(''); setReferenceIds('');
      setNotice('Structured information published. Inventory was not changed.'); setRevision(function (value) { return value + 1; });
    } catch (error) { setNotice('Publish rejected. Check audience, content and references.'); }
  }

  return <div className="cp-information-workspace"><p role="status">{notice}</p>
    <section className="cp-share-compose" aria-label="Publish structured information"><h3>Publish game knowledge</h3>
      <div className="cp-information-chip-field"><b>Source</b>{SOURCES.map(function (value) { return <ActionPill key={value} primary={source === value} onClick={function () { setSource(value); }}>{label(value)}</ActionPill>; })}</div>
      <div className="cp-information-chip-field"><b>Recipients</b>{['PARTY', 'SELECTED_PLAYERS', 'SINGLE_PLAYER', 'DM_ONLY'].map(function (value) { return <ActionPill key={value} primary={scope === value} onClick={function () { setScope(value); }}>{label(value)}</ActionPill>; })}</div>
      {(scope === 'SELECTED_PLAYERS' || scope === 'SINGLE_PLAYER') ? <div className="cp-information-chip-field"><b>Players</b>{((context && context.players) || []).map(function (player) { return <ActionPill key={player.player_id} primary={recipients.indexOf(player.player_id) >= 0} onClick={function () { toggleRecipient(player.player_id); }}>{player.display_name}</ActionPill>; })}</div> : null}
      <div className="cp-share-grid">
        <label>Card type<select value={type} onChange={function (event) { setType(event.target.value); }}>{INFORMATION_TYPES.map(function (value) { return <option key={value} value={value}>{label(value)}</option>; })}</select></label>
        <label>Title<input maxLength="240" value={title} onChange={function (event) { setTitle(event.target.value); }} /></label>
        <label>Player-safe summary<textarea value={summary} onChange={function (event) { setSummary(event.target.value); }} /></label>
        <label>Context / location<textarea value={detail} onChange={function (event) { setDetail(event.target.value); }} /></label>
        <label>Items, one per line<textarea value={items} onChange={function (event) { setItems(event.target.value); }} /></label>
        <label>Currency<input value={currency} onChange={function (event) { setCurrency(event.target.value); }} /></label>
        <label>Ownership state<input value={ownership} onChange={function (event) { setOwnership(event.target.value); }} /></label>
        <label>Existing item IDs, comma separated<input value={referenceIds} onChange={function (event) { setReferenceIds(event.target.value); }} /></label>
      </div><div className="cp-action-row"><ActionPill primary disabled={!title.trim() || !summary.trim()} onClick={publish}>Publish Information</ActionPill></div>
    </section>
    {context ? <section key={revision} aria-label="Master information reference"><h3>Master information</h3><InformationReference mode="dm" campaignId={context.campaign_id} encounterId={context.encounter_id} contextPlayers={context.players} /></section> : null}
  </div>;
}

export function PlayerInformationWorkspace({ campaignId }) {
  return <div className="cp-information-workspace"><InformationReference mode="player" campaignId={campaignId} /></div>;
}

export function InformationChip({ mode, campaignId }) {
  const { openLayer } = useLayeredModal();
  const player = mode === 'player';
  return <button type="button" className="cp-workspace-chip" onClick={function () { openLayer({ title: 'Information', eyebrow: player ? 'Player Reference' : 'DM Master Reference', subtitle: player ? 'Persistent game knowledge authorised for your player account.' : 'Publish and maintain structured game facts while play continues normally.', content: player ? <PlayerInformationWorkspace campaignId={campaignId} /> : <DmInformationWorkspace /> }); }}>Information</button>;
}

export default InformationChip;
